import { ReactNode, useMemo } from 'react';
import _ from 'lodash';

import { Button } from '../../shared/Button';
import { Chip } from '../../shared/Chip';
import { TableColumn } from '../../shared/Table/TableColumn';
import { TEMPLATE_DISPLAY_NAMES } from '../model/PostTemplates';
import { PortalPublication } from '../PortalPublication';
import { PostThumbnail } from '../PostThumbnail';
import Tooltip from '@mui/material/Tooltip';

import { useSelectedLanguage } from '../../hooks/selectedLanugage';

import { toHTMLDate } from '../../utils/dates';

import { getPublicationStatus } from '../portalUtils';
import styles from './PortalPublicationFeedTable.module.scss';
import {
    getPublicationTranslationIssue,
    getPublicationTranslationIssueTitle,
    type PublicationTranslationIssue,
} from './publicationTranslationWarnings';
import { useLocalizationsById } from './useLocalizationsById';

import ChevronDoubleDown from '../../assets/chevron-double-down.svg';
import ChevronDoubleRight from '../../assets/chevron-double-right.svg';
import ChevronDoubleUp from '../../assets/chevron-double-up.svg';

const VARIANT_TAG = 'variant';

export type PublicationRowColumns =
    | 'thumbnail'
    | 'indicator'
    | 'position'
    | 'name'
    | 'templateName'
    | 'start'
    | 'end'
    | 'status'
    | 'actions';

interface PortalPublicationFeedTableProps {
    originalPublications: PortalPublication[];
    publications: PortalPublication[];
    onStartTimeChange?: (id: string, time: Date) => void;
    onEndTimeChange?: (id: string, time: Date) => void;
    locked?: boolean;
}
export const PortalPublicationFeedTable = ({
    originalPublications,
    publications,
    onStartTimeChange,
    onEndTimeChange,
    locked,
}: PortalPublicationFeedTableProps) => {
    const { selectedLanguage } = useSelectedLanguage();
    const { localizationsById, isLoading: isLocalizationsLoading } =
        useLocalizationsById();

    return (
        <table className={styles.table}>
            <thead>
                <tr>
                    <TableColumn>{/* Indicator */}</TableColumn>
                    <TableColumn>{/* Thumbnail */}</TableColumn>
                    <TableColumn>Position</TableColumn>
                    <TableColumn>Name</TableColumn>
                    <TableColumn>Post Type</TableColumn>
                    <TableColumn>Start</TableColumn>
                    <TableColumn>End</TableColumn>
                    <TableColumn>Status</TableColumn>
                    <TableColumn>{/*Actions*/}</TableColumn>
                </tr>
            </thead>
            <tbody>
                {publications
                    .toSorted((a, b) => a.position - b.position)
                    .map((publication) => {
                        const translationIssue =
                            isLocalizationsLoading || !localizationsById
                                ? null
                                : getPublicationTranslationIssue(
                                      publication,
                                      selectedLanguage,
                                      localizationsById,
                                  );

                        return (
                            <PublicationRow
                                key={publication.id}
                                publication={publication}
                                onStartTimeChange={(time) =>
                                    onStartTimeChange?.(publication.id, time)
                                }
                                onEndTimeChange={(time) =>
                                    onEndTimeChange?.(publication.id, time)
                                }
                                status={
                                    <PublicationStatus
                                        originalPublications={
                                            originalPublications
                                        }
                                        publication={publication}
                                    />
                                }
                                translationIssue={translationIssue}
                                locked={locked}
                            />
                        );
                    })}
            </tbody>
        </table>
    );
};

interface PublicationStatusProps {
    originalPublications: PortalPublication[];
    publication: PortalPublication;
}
const PublicationStatus = ({
    originalPublications,
    publication,
}: PublicationStatusProps) => {
    const originalScheduleMap = useMemo(() => {
        return new Map(
            originalPublications.map((publication) => [
                publication.id,
                publication,
            ]) ?? [],
        );
    }, [originalPublications]);

    const isNew = !originalScheduleMap.has(publication.id);
    const isModified =
        !isNew &&
        !_.isEqual(publication, originalScheduleMap.get(publication.id));

    const publicationStatus = getPublicationStatus(publication);

    if (publicationStatus === 'expired') {
        return <Chip color="danger">Expired</Chip>;
    }
    if (isNew) {
        return <Chip color="positive">Added</Chip>;
    }
    if (isModified) {
        return <Chip color="primary">Modified</Chip>;
    }
    return (
        <>
            {publicationStatus === 'scheduled' && (
                <Chip color="secondary">Scheduled</Chip>
            )}
            {['published', 'expiring'].includes(publicationStatus) && (
                <Chip color="primary">Published</Chip>
            )}
        </>
    );
};

interface PublicationRowProps {
    publication: PortalPublication;
    status: ReactNode;
    translationIssue: PublicationTranslationIssue | null;
    onStartTimeChange?: (date: Date) => void;
    onEndTimeChange?: (date: Date) => void;
    locked?: boolean;
}

const publicationStatusToIcon = {
    scheduled: (
        <img src={ChevronDoubleUp} className={styles.indicatorScheduled} />
    ),
    published: <div className={styles.indicatorPlaceholder} />,
    expiring: (
        <img src={ChevronDoubleRight} className={styles.indicatorExpire} />
    ),
    expired: <img src={ChevronDoubleDown} className={styles.indicatorExpire} />,
};

const PublicationRow = ({
    publication,
    status,
    translationIssue,
    onStartTimeChange,
    onEndTimeChange,
    locked,
}: PublicationRowProps) => {
    const publicationStatus = getPublicationStatus(publication);
    const hasVariantTag = publication.post.postTags?.includes(VARIANT_TAG);

    return (
        <tr style={{ opacity: hasVariantTag ? 0.6 : 1 }}>
            <td>{publicationStatusToIcon[publicationStatus]}</td>
            <td className={styles.thumbnailColumn}>
                <PostThumbnail
                    className={styles.thumbnail}
                    postData={publication.post.payload}
                    templateId={publication.post.templateId}
                    height={48}
                />
            </td>
            <td>{publication.position + 1}</td>
            <td>
                {publication.post.id}
                {hasVariantTag && <Chip color="secondary">Variant</Chip>}
                {translationIssue && (
                    <Tooltip
                        title={getPublicationTranslationIssueTitle(
                            translationIssue,
                        )}
                        enterDelay={0}
                        arrow
                    >
                        <span
                            className={styles.translationWarningChip}
                            tabIndex={0}
                            aria-label={getPublicationTranslationIssueTitle(
                                translationIssue,
                            )}
                        >
                            <Chip color="danger">Missing translations</Chip>
                        </span>
                    </Tooltip>
                )}
            </td>
            <td>
                {TEMPLATE_DISPLAY_NAMES[publication.post.templateId] ??
                    publication.post.templateId}
            </td>
            <td>
                <input
                    title="Start Time"
                    type="datetime-local"
                    value={toHTMLDate(publication.startTime)}
                    onChange={(e) => {
                        if (e.target.value.length > 0) {
                            onStartTimeChange?.(new Date(e.target.value));
                        }
                    }}
                    required
                    disabled={locked}
                />
            </td>
            <td>
                <input
                    title="End Time"
                    type="datetime-local"
                    value={toHTMLDate(publication.endTime ?? undefined)}
                    onChange={(e) => {
                        onEndTimeChange?.(new Date(e.target.value));
                    }}
                    disabled={locked}
                />
            </td>
            <td>{status}</td>
            <td>
                <div className={styles.actionsCell}>
                    <Button
                        color="secondary"
                        onClick={() => {
                            if (onEndTimeChange) {
                                // Truncate seconds to be consistent with the other date input elements
                                const date = new Date();
                                date.setSeconds(0);
                                date.setMilliseconds(0);
                                onEndTimeChange(date);
                            }
                        }}
                        disabled={locked}
                    >
                        Unpublish
                    </Button>
                </div>
            </td>
        </tr>
    );
};
